<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useSmarticoMiniGames } from '@/stores/smartico/miniGames';
import { useSmarticoApi } from '@/composables/useSmarticoApi';
import { useSmarticoInit } from '@/stores/smartico/init';
import { useAuthStore } from '@/stores/auth';
import { useAuth } from '@/composables/useAuth';

// Esta rota (que já existe em prod) tem dois modos — rotas NOVAS são bloqueadas
// no deploy, então a roleta antiga reusa este mesmo path via query:
//
// - padrão (deep-link/menu): abre a roletinha NOVA (modal global) e volta pra
//   home. O id do link vira o fallback pra roleta antiga.
// - ?legacy=1: renderiza a roleta ANTIGA (iframe do Smartico inline). É pra onde
//   o fallback navega quando o core não tem roleta pra esse user
//   (ver stores/roulette.ts → openLegacyRoulette).
const route = useRoute();
const router = useRouter();
const { standardTitleSuffix } = getStandardSeoSuffixes();

const isLegacy = computed(() => route.query.legacy !== undefined);

const miniGameId = computed(() =>
  parseInt(String((route.params as { id: string }).id)),
);

const smarticoMiniGamesStore = useSmarticoMiniGames();
const { loadMiniGames } = smarticoMiniGamesStore;
const { miniGames } = storeToRefs(smarticoMiniGamesStore);
const { smarticoInstance } = useSmarticoApi();
const { isSuccessCheckUserIdentify } = storeToRefs(useSmarticoInit());

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const { openLogin } = useAuth();

const isLoading = ref(true);
const smarticoIframeRef = ref<HTMLIFrameElement | null>(null);

const currentMiniGame = computed(() =>
  miniGames.value.find((game) => game.id === miniGameId.value),
);

useSeo({
  title: isLegacy.value
    ? `${currentMiniGame.value?.name || 'Roletinha'} ${standardTitleSuffix}`
    : `Roletinha ${standardTitleSuffix}`,
});

// Modo padrão: abre o modal novo e volta pra home. O id do link é o fallback
// pra roleta antiga caso o core não tenha roleta pra esse user.
onMounted(() => {
  if (isLegacy.value) return;

  useRouletteStore().open({ fallbackMiniGameId: miniGameId.value });
  navigateTo(localeLink('/'), { replace: true });
});

// Fallback: o usuário chegou aqui pela roletinha (home/menu), não pela lista de
// mini-games — então "voltar" leva pra home.
const handleBackNavigation = () => {
  router.push(localeLink('/'));
};

const initializeMiniGame = async () => {
  if (!authStore.isAuthenticated) {
    openLogin();

    return;
  }

  if (isNaN(miniGameId.value)) {
    return;
  }

  if (miniGames.value.length === 0) {
    await loadMiniGames();
  }

  await new Promise<void>((resolve) => {
    const checkSmarticoInstanceReady = setInterval(() => {
      if (
        smarticoInstance.value &&
        typeof smarticoInstance.value.miniGame === 'function' &&
        isSuccessCheckUserIdentify.value
      ) {
        clearInterval(checkSmarticoInstanceReady);
        resolve();
      }
    }, 50);
  });

  await new Promise<void>((resolve) => {
    const checkIframeReady = setInterval(() => {
      if (smarticoIframeRef.value) {
        clearInterval(checkIframeReady);
        resolve();
      }
    }, 50);
  });

  const playOptions: {
    iframe?: string;
    inline?: boolean;
    height?: string;
    width?: string;
    zoom?: number;
    theme?: string;
  } = {
    iframe: 'smartico-minigame-frame',
    inline: true,
  };

  if (window._smartico && typeof window._smartico.miniGame === 'function') {
    window._smartico.miniGame(miniGameId.value, playOptions);
    if (smarticoIframeRef.value) {
      smarticoIframeRef.value.style.display = 'block';
      smarticoIframeRef.value.style.width = '153.85%';
      smarticoIframeRef.value.style.height = '100vh';
      smarticoIframeRef.value.style.transform = 'scale(0.65)';
      smarticoIframeRef.value.style.transformOrigin = 'top left';
      smarticoIframeRef.value.style.marginBottom = '-35vh';
    }
  }

  if (
    smarticoInstance.value &&
    typeof smarticoInstance.value.on === 'function'
  ) {
    smarticoInstance.value.on('gf_closing', () => {
      router.push(localeLink('/'));
    });
  }
};

// Só o modo legacy inicializa o iframe do Smartico. No modo padrão a rota já
// redirecionou pra home no onMounted, então esta lógica fica inerte.
watch(
  [isAuthenticated, smarticoInstance],
  async ([authenticated, smarticoApi]) => {
    if (!isLegacy.value) return;

    if (!authenticated) {
      openLogin();
      router.push(localeLink('/'));

      return;
    }

    if (authenticated && smarticoApi) {
      isLoading.value = false;

      await initializeMiniGame();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="isLegacy"
    class="mini-game-page"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      :title="currentMiniGame?.name || 'Roletinha'"
    />

    <div class="mini-game-main-content-wrapper">
      <div
        v-if="isLoading"
        class="loading-indicator text-texts text-[14px]"
      >
        Carregando roletinha...
      </div>
      <div
        v-else
        class="games mini-game-content-wrapper"
      >
        <section class="spacing">
          <div class="games_content">
            <div class="mini-game-iframe-container">
              <iframe
                id="smartico-minigame-frame"
                ref="smarticoIframeRef"
                class="smartico-minigame-iframe"
                title="Smartico Mini-game"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
  <div v-else />
</template>

<style scoped lang="scss">
.mini-game-page {
  @apply flex flex-col;
}

.mini-game-main-content-wrapper {
  @apply w-full max-w-full;
}

.loading-indicator {
  @apply text-xl text-center my-10;
}

.games {
  @apply flex flex-col xl:pt-5;

  .spacing {
    @apply p-3 pt-0 px-0;
  }

  .games_content {
    @apply flex justify-center bg-bg-primary;

    .mini-game-iframe-container {
      width: 100%;
      height: auto;
      border: 1px solid #cccccc10;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .smartico-minigame-iframe {
      flex-grow: 1;
    }
  }
}
</style>
